"use client"

import * as React from "react"

import {
  EDGE_RESIZE_HANDLE_CLASS,
  EDGE_RESIZE_HANDLE_LEFT_CLASS,
  startEdgeResizeDrag,
  verticalResizeSeparatorAria,
} from "../../lib/edge-resize-handle"
import { usePersistedState } from "../../lib/persisted-state"
import {
  NAV_FLYOUT_CHROME,
  NAV_FLYOUT_INSET_LEFT,
  NAV_FLYOUT_INSET_RIGHT,
  NAV_FLYOUT_INSET_Y,
  SECONDARY_PANEL_BODY_STACK,
  navFlyoutMaxWidth,
} from "../../lib/nav-flyout-inset"
import { cn } from "../../lib/utils"

export const SECONDARY_PANEL_WIDTH_DEFAULT = 256
export const SECONDARY_PANEL_WIDTH_MIN = 200
export const SECONDARY_PANEL_WIDTH_MAX = 480
const SECONDARY_PANEL_WIDTH_KEY = "shell:secondary-panel-width"

/** Default width for the Ask Leo right rail (panel mode). */
export const ASK_LEO_PANEL_WIDTH_DEFAULT = 320
export const ASK_LEO_PANEL_WIDTH_KEY = "shell:ask-leo-panel-width"

function clampSecondaryPanelWidth(width: number) {
  return Math.min(
    SECONDARY_PANEL_WIDTH_MAX,
    Math.max(SECONDARY_PANEL_WIDTH_MIN, width),
  )
}

export interface NestedSecondaryPanelShellProps {
  /** When false, the shell is visually hidden (no width). */
  open: boolean
  /** Icon-only rail (same width token as primary `Sidebar` collapsible icon mode). */
  compact: boolean
  /**
   * High-zoom / mobile flyout — fixed panel stacked above the shell edge
   * (same anchor, higher z-index). Takes no flex layout space.
   */
  overlay?: false | "mobile" | "desktop"
  /** Rail side — secondary nav is left; Ask Leo is right. */
  side?: "left" | "right"
  /** `data-slot` on the outer `<nav>` — defaults to `secondary-panel`. */
  dataSlot?: string
  /** Persisted width key — defaults to the library secondary panel. */
  widthPersistKey?: string
  /** Initial width when no persisted value exists. */
  widthDefault?: number
  children: React.ReactNode
  /** Forwarded to the outer `<nav>`. */
  "aria-label"?: string
  className?: string
  /** Inner scroll wrapper — Ask Leo passes `overflow-hidden` for a pinned composer. */
  contentClassName?: string
}

/**
 * Shared chrome for a nested hub rail — full width vs icon rail.
 * Fill uses `--secondary-panel-bg` — one step lighter than `--sidebar` (elevation 1).
 */
export function NestedSecondaryPanelShell({
  open,
  compact,
  overlay = false,
  side = "left",
  dataSlot = "secondary-panel",
  widthPersistKey = SECONDARY_PANEL_WIDTH_KEY,
  widthDefault = SECONDARY_PANEL_WIDTH_DEFAULT,
  children,
  "aria-label": ariaLabel = "Secondary navigation",
  className,
  contentClassName,
}: NestedSecondaryPanelShellProps) {
  const [width, setWidth] = usePersistedState(
    widthPersistKey,
    widthDefault,
  )
  const [isResizing, setIsResizing] = React.useState(false)
  const widthRef = React.useRef(width)
  React.useEffect(() => {
    widthRef.current = width
  })

  const expandedWidth = clampSecondaryPanelWidth(width)
  const isOverlay = overlay !== false
  const showResizeHandle = open && !compact && !isOverlay
  const layoutExpanded = open && (!compact || isOverlay)

  const overlayStackClass = isOverlay
    ? cn(
        side === "right" ? NAV_FLYOUT_INSET_RIGHT : NAV_FLYOUT_INSET_LEFT,
        NAV_FLYOUT_INSET_Y,
      )
    : undefined

  const overlayMaxWidth = isOverlay
    ? navFlyoutMaxWidth(expandedWidth)
    : undefined

  const startResize = React.useCallback(
    (event: React.MouseEvent) => {
      const startW = clampSecondaryPanelWidth(widthRef.current)
      setIsResizing(true)
      startEdgeResizeDrag(
        event,
        clientX => {
          const delta =
            side === "right" ? event.clientX - clientX : clientX - event.clientX
          setWidth(clampSecondaryPanelWidth(startW + delta))
        },
        () => setIsResizing(false),
      )
    },
    [setWidth, side],
  )

  const resizeHandleClass =
    side === "right" ? EDGE_RESIZE_HANDLE_LEFT_CLASS : EDGE_RESIZE_HANDLE_CLASS

  return (
    <nav
      aria-label={ariaLabel}
      data-slot={dataSlot}
      data-state={open ? "open" : "closed"}
      data-side={side}
      data-layout={open ? (layoutExpanded ? "expanded" : "icon") : "closed"}
      data-overlay={isOverlay ? overlay : undefined}
      data-resizing={isResizing ? "true" : undefined}
      style={
        layoutExpanded
          ? isOverlay
            ? {
                width: expandedWidth,
                minWidth: expandedWidth,
                maxWidth: overlayMaxWidth,
              }
            : {
                width: expandedWidth,
                minWidth: expandedWidth,
                maxWidth: expandedWidth,
              }
          : undefined
      }
      suppressHydrationWarning
      className={cn(
        "flex min-h-0 flex-col",
        !isOverlay && showResizeHandle && "relative overflow-hidden",
        !isOverlay && "transition-[width,margin,opacity] duration-200 ease-linear",
        isOverlay &&
          cn(
            "fixed z-[70] flex min-h-0 flex-col bg-[var(--secondary-panel-bg)]",
            NAV_FLYOUT_CHROME,
            "transition-[opacity,transform] duration-200 ease-linear",
            overlayStackClass,
          ),
        isResizing && "transition-none",
        open
          ? isOverlay
            ? cn("pointer-events-auto opacity-100", className)
            : cn(
                // No ring/border — `SidebarInset` and the primary `Sidebar` (inset
                // variant) both use `shadow-sm` alone for elevation; a ring here
                // read as an unexplained extra border inconsistent with the rest
                // of the inset shell chrome.
                "shrink-0 mx-2 self-stretch min-h-0 overflow-hidden rounded-xl shadow-sm bg-[var(--secondary-panel-bg)]",
                compact ? "w-12 min-w-12 max-w-12" : "min-w-0",
                className,
              )
          : isOverlay
            ? "pointer-events-none fixed z-[70] opacity-0"
            : "h-0 min-h-0 shrink overflow-hidden border-0 p-0 m-0 min-w-0 w-0 max-w-0 opacity-0 pointer-events-none",
      )}
    >
      <div
        className={cn(
          SECONDARY_PANEL_BODY_STACK,
          open ? "min-w-0" : "hidden min-w-0 w-0 p-0",
          contentClassName,
        )}
      >
        {children}
      </div>
      {showResizeHandle ? (
        <div
          {...verticalResizeSeparatorAria({
            label: side === "right" ? "Resize Ask Leo panel" : "Resize secondary panel",
            valueNow: expandedWidth,
            valueMin: SECONDARY_PANEL_WIDTH_MIN,
            valueMax: SECONDARY_PANEL_WIDTH_MAX,
          })}
          data-slot="secondary-panel-resize-handle"
          onMouseDown={startResize}
          className={resizeHandleClass}
        />
      ) : null}
    </nav>
  )
}
